import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Sparkles } from "lucide-react";

/**
 * GrassOfferBanner — promotional banner for the Artificial Grass introductory offer.
 * Standalone, self-contained, unrelated to the Artificial Grass Pricing Calculator.
 * Placement: below hero on Home (after trust points) + below hero on ServiceDetail (artificial-grass).
 */
export default function GrassOfferBanner() {
  return (
    <section className="py-10 lg:py-14 bg-white" data-testid="grass-offer-banner">
      <div className="max-w-6xl mx-auto px-5 lg:px-8">
        <div className="relative overflow-hidden rounded-3xl bg-dark text-white noise shadow-2xl shadow-emerald-900/20">
          {/* Corner ribbon (top-right) — desktop/tablet */}
          <div className="pointer-events-none absolute -right-16 top-7 z-10 hidden sm:block rotate-45" aria-hidden="true">
            <div className="bg-gradient-to-r from-amber-400 to-yellow-500 text-dark font-bold text-[11px] tracking-[0.2em] uppercase px-16 py-1.5 shadow-md">
              Limited Offer
            </div>
          </div>

          {/* Mobile pill (visible on small screens) */}
          <div className="sm:hidden absolute top-4 right-4 z-10 inline-flex items-center gap-1.5 bg-gradient-to-r from-amber-400 to-yellow-500 text-dark font-bold text-[10px] tracking-widest uppercase px-3 py-1 rounded-full shadow">
            <Sparkles size={11}/> Limited Offer
          </div>

          {/* Ambient lime glow */}
          <div className="absolute -left-24 -top-24 w-96 h-96 rounded-full bg-lime/20 blur-3xl" aria-hidden="true" />
          <div className="absolute -right-24 -bottom-24 w-96 h-96 rounded-full bg-emerald-500/15 blur-3xl" aria-hidden="true" />

          <div className="relative p-7 sm:p-10 lg:p-14 grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            {/* Left: copy */}
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-1.5 text-lime text-[11px] tracking-[0.25em] font-semibold uppercase mb-4">
                <Sparkles size={12} /> Introductory promotion
              </div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-tight">
                Artificial Grass Installation<br />
                <span className="text-lime italic">from £45<span className="text-white/70 text-2xl sm:text-3xl align-top">/m²</span></span>
              </h2>

              <ul className="mt-6 grid sm:grid-cols-2 gap-x-6 gap-y-2.5 text-[15px] text-white/85">
                <li className="flex items-start gap-2"><Check size={16} className="text-lime mt-0.5 shrink-0" /> First 10 bookings only</li>
                <li className="flex items-start gap-2"><Check size={16} className="text-lime mt-0.5 shrink-0" /> Selected straightforward installations</li>
                <li className="flex items-start gap-2"><Check size={16} className="text-lime mt-0.5 shrink-0" /> Free site survey</li>
                <li className="flex items-start gap-2"><Check size={16} className="text-lime mt-0.5 shrink-0" /> Professional installation</li>
              </ul>
            </div>

            {/* Right: CTA */}
            <div className="lg:col-span-5 lg:justify-self-end w-full lg:w-auto">
              <Link
                to="/quote"
                data-testid="grass-offer-cta"
                className="offer-pulse group relative inline-flex items-center justify-center gap-2 bg-lime hover:bg-lime/90 text-dark font-bold text-lg px-8 py-5 rounded-full w-full lg:w-auto shadow-lg shadow-lime/30 transition"
              >
                Claim My Free Quote
                <ArrowRight size={20} className="transition-transform group-hover:translate-x-1" />
              </Link>
              <p className="text-[11px] text-white/45 mt-4 leading-relaxed max-w-md">
                *Offer applies to selected straightforward installations only. Final price depends on site survey, access, excavation depth, existing ground conditions, edging requirements and the artificial grass selected. Terms and conditions apply.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
